import { useState, useEffect, useRef } from "react";
import { useParams, Navigate, useSearchParams } from "react-router-dom";
import { Table, TableMenu } from "../../components/Table";
import {
  FaPrint,
  FaChevronUp, FaFilter,
  FaChevronDown, FaSearch,
  FaArrowLeft, FaInfoCircle,
  FaCheckCircle,
} from "react-icons/fa";
// import DatePicker from "react-datepicker";

import {
  getFilteredSales,
  computeBookingSummary,
  groupOrderItemsByType
} from "../../utils/sales-summary";
import Order from "../../models/Order";
import { initAllStores } from "../../utils/cache";
import { formatDate, getTodayRange } from "../../utils/date";
import FullPageSpinner from "../../components/FullPageSpinner";
import { SaleItem } from "../../types/sales";
import BookedRoom from "../../models/BookedRoom";
import { attachRoomDataToBookings } from "../../utils/booking-helper";
// import OrderItem from "../models/OrderItem";
import { isEmptyObject } from "../../utils/helper-function";
import { useToast } from "../../context/ToastContext";
import { PAGINATION_PAGE_NUMBER } from "../../constants";
import { sendRequest } from "../../utils/api";
import SalesDetailsModal from "../../Modal/accounting/SaleDetailsModal";
import { terminal } from "../../utils/user-helper";


type ExtendedServiceType = "rooms" | "foods" | "drinks" | "laundries" | "games";
const categories: ExtendedServiceType[] = ["rooms", "drinks", "foods", "laundries", "games"];

const salesMap = {
  foods: { title: "Food" },
  drinks: { title: "Drink" },
  games: { title: "Game" },
  laundries: { title: "Laundry" },
  rooms: { title: "Room" },
};

export default function SalesDetailPage() {

  const [terminalId] = useState(() => terminal());

  const [loading, setLoading] = useState(true);
  // const [showDuplicateNotice, setShowDuplicateNotice] = useState(true);
  const { type } = useParams<{ type: ExtendedServiceType }>();
  const [activeType, setActiveType] = useState<ExtendedServiceType>(type as ExtendedServiceType);
  const { showToast } = useToast();

  const [searchParams] = useSearchParams();

  const dt = searchParams.get("date");

  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState({ start: dt, end: dt });
  const [selectedCategory] = useState("");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>("all");

  const [bookingSales, setBookingSales] = useState<BookedRoom[]>([]);
  const [allBookingSales, setAllBookingSales] = useState<BookedRoom[]>([]);

  const [summaryVisible, setSummaryVisible] = useState(true);
  const [showSummaryGuide, setShowSummaryGuide] = useState(false);
  const [saleDetailModal, setSaleDetailModal] = useState(false);


  // const [tableActionMenu, setTableActionMenu] = useState(true);

  const [orderSales, setOrderSales] = useState<SaleItem[]>([]);
  const [allOrderSales, setAllOrderSales] = useState<{
    drinks: {
      allSales: SaleItem[],
      paidSales: SaleItem[],
      pendingSales: SaleItem[],
    },
    foods: {
      allSales: SaleItem[],
      paidSales: SaleItem[]
      pendingSales: SaleItem[],
    },
    laundries: {
      allSales: SaleItem[],
      pendingSales: SaleItem[],
      paidSales: SaleItem[]
    },
    games: {
      allSales: SaleItem[],
      pendingSales: SaleItem[],
      paidSales: SaleItem[]
    },
  }>({
    drinks: { allSales: [], pendingSales: [], paidSales: [] },
    foods: { allSales: [], pendingSales: [], paidSales: [] },
    laundries: { allSales: [], pendingSales: [], paidSales: [] },
    games: { allSales: [], pendingSales: [], paidSales: [] },
    // rooms: [],
  });

  const [sale, setSale] = useState<BookedRoom | SaleItem>({} as BookedRoom | SaleItem);
  const [saleDate, setSaleDate] = useState("");

  const defaultSummary = {
    totalSold: 0,
    totalCash: 0,
    totalPos: 0,
    totalTransfer: 0,
    totalPending: 0,
    totalDiscount: 0,
  };

  type Summary = typeof defaultSummary;

  const [orderSummary, setOrderSummary] = useState<Record<ServiceType, Summary>>({
    drinks: { ...defaultSummary },
    foods: { ...defaultSummary },
    laundries: { ...defaultSummary },
    games: { ...defaultSummary },
  });

  const [summary, setSummary] = useState({
    totalSold: 0,
    totalCash: 0,
    totalPos: 0,
    totalTransfer: 0,
    totalPending: 0,
    totalDiscount: 0,
  });
  const printRef = useRef<HTMLDivElement>(null);

  // ✅ Filter logic
  const filterSales = (
    data: (SaleItem | BookedRoom)[],
    orderSummary?: Record<ServiceType, Summary>,
    // selectedService?: ExtendedServiceType
  ) => {

    let filtered = [...data];

    // ✅ Search filter
    if (search) {
      const term = search.toLowerCase();

      if (activeType === "rooms") {
        filtered = filtered.filter((i) =>
          "guestName" in i ? i.guestName.toLowerCase().includes(term) : false
        );
      } else {
        filtered = filtered.filter((i) =>
          "name" in i ? i.name.toLowerCase().includes(term) : false
        );
      }
    }

    if (activeFilter && activeFilter.toLowerCase() !== "all") {
      filtered = filtered.filter((item) => {
        // Only filter if paymentStatus exists
        if (!("paymentStatus" in item)) return true;
        const status = (item.paymentStatus as string).trim().toLowerCase();
        return status === activeFilter.toLowerCase();
      });
    }

    // ✅ Apply to correct state
    if (activeType === "rooms") {

      const mappedBooking = (filtered as BookedRoom[]).map(bk => ({
        ...bk,
        checkInDate: formatDate(bk.checkInDate),
        checkOutDate: formatDate(bk.checkOutDate),
      }));

      // console.log();

      setBookingSales(mappedBooking);

      const {
        bookCash,
        bookTransfer,
        bookPending,
        bookDiscount,
        bookPos,
        bookUnpaidDiscount
      } = computeBookingSummary(data as BookedRoom[]);

      setSummary({
        totalSold: bookCash + bookPos + bookTransfer,
        totalCash: bookCash,
        totalPos: bookPos,
        totalTransfer: bookTransfer,
        totalPending: bookPending,
        totalDiscount: bookDiscount + bookUnpaidDiscount
      });
    } else {
      setOrderSales(filtered as SaleItem[]);

      if (!isEmptyObject(orderSummary) && orderSummary) {
        setOrderSummary(orderSummary);
        setSummary(orderSummary[activeType]);
      }
    }
  };

  // ✅ Fetch sales for the given date range
  const fetchSalesForRange = async (
    orders: Order[], bookings: BookedRoom[]
  ) => {
    try {
      setLoading(true);
      const { drinks, foods, games, laundries } = await groupOrderItemsByType(orders);
      const bookedRooms = await attachRoomDataToBookings(bookings);

      // console.log(foods.allSales)
      const allOrders = {
        foods: {
          allSales: foods.allSales,
          paidSales: foods.paidSales,
          pendingSales: foods.pendingSales
        },
        drinks: {
          allSales: drinks.allSales,
          paidSales: drinks.paidSales,
          pendingSales: drinks.pendingSales
        },
        games: {
          allSales: games.allSales,
          paidSales: games.paidSales,
          pendingSales: games.pendingSales
        },
        laundries: {
          allSales: laundries.allSales,
          paidSales: laundries.paidSales,
          pendingSales: laundries.pendingSales
        },
      };

      const services = { drinks, foods, laundries, games };

      const allOrderSummary = Object.fromEntries(
        Object.entries(services).map(([key, data]) => [
          key,
          {
            totalSold: data.summary.totals,
            totalCash: data.summary.cash,
            totalPos: data.summary.pos,
            totalTransfer: data.summary.transfer,
            totalPending: data.summary.pending,
            totalDiscount: data.summary.paidDiscount
          },
        ])
      ) as Record<ServiceType, Summary>;

      // Store everything once
      setAllOrderSales(allOrders);
      setAllBookingSales(bookedRooms);

      setOrderSummary(allOrderSummary);

      // Apply filters to whichever tab is currently active
      if (activeType === "rooms") {
        filterSales(bookedRooms);
      } else {
        const paymentStatus = activeFilter.toLocaleLowerCase();
        let items: SaleItem[] = getFilteredSales(allOrders, activeType, paymentStatus);
        filterSales(items, allOrderSummary);
      }
    } catch (err: any) {
      // console.log(err);
    } finally {
      setLoading(false);
    }
  };


  const getTodaySales = async (): Promise<{ orders: Order[], bookings: BookedRoom[] }> => {
    const { start, end } = getTodayRange();
    const cache = await initAllStores();

    const orders: Order[] = await cache.getAllBy(
      "orders", { terminal: terminalId }, "createdAt", "asc",
      { field: "createdAt", start, end }
    );

    const bookings: BookedRoom[] = await cache.getAllBy(
      "bookedRooms", { terminal: terminalId }, "createdAt",
      "asc", { field: "createdAt", start, end }
    );

    return { orders, bookings }
  }

  // ✅ Hide sales summary by default on mobile devices (screen width < 1024px).
  // Users can still toggle it manually using the info button.
  useEffect(() => {
    if (window.innerWidth < 802) {
      setSummaryVisible(false);
    }
  }, []);

  // console.log(activeFilter)
  // ✅ Re-filter when filters or search change
  useEffect(() => {
    if (activeType === "rooms") {
      if (allBookingSales.length > 0) filterSales(allBookingSales);
      else setAllBookingSales([]);
    } else {
      const paymentStatus = activeFilter.toLocaleLowerCase();
      let items: SaleItem[] = getFilteredSales(allOrderSales, activeType, paymentStatus);
      if (items.length > 0) filterSales(items, orderSummary);
      else filterSales([], orderSummary);
    }
  }, [search, selectedCategory, activeType, activeFilter]);


  // Handle apply date filter
  useEffect(() => {
    if (activeType && dateRange.start && dateRange.end) {
      handleApplyDate();
    }
  }, [activeType]);

  const handleClear = async () => {
    setSearch("");
    setDateRange({ start: "", end: "" });

    if (dateRange.start && dateRange.end) {
      const { orders, bookings } = await getTodaySales();
      await fetchSalesForRange(orders, bookings);
    }
  };

  const handleApplyDate = async () => {
    if (!dateRange.start || !dateRange.end) return;
    setLoading(true);

    let salesType: string = "";
    const orders = ["foods", "drinks", "games", "laundries"];
    if (orders.includes(activeType)) salesType = "orders";
    else if (activeType === "rooms") salesType = "bookedRooms";

    // console.log(dateRange)

    try {
      const data = await sendRequest("POST", `/sync/bills?terminal=${terminalId}`, {
        [salesType]: [], date: dateRange
      });

      // console.log(data.orders)
      await fetchSalesForRange(data.orders ?? [], data.bookedRooms ?? []);
    } catch (err: any) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!printRef.current) return;
    const printContents = printRef.current.innerHTML;
    const printWindow = window.open("", "", "width=900,height=650");
    printWindow?.document.write(`
      <html>
        <head>
          <title>${salesMap[activeType].title} Sales Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { text-align: center; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: center; }
            th { background: #f4f4f4; }
          </style>
        </head>
        <body>
          <h1>${salesMap[activeType].title} Sales Report</h1>
          ${printContents}
        </body>
      </html>
    `);
    printWindow?.document.close();
    printWindow?.print();
  };

  if (!type || !salesMap[type]) return <Navigate to="/404" replace />;

  return (
    <div className="p-5 space-y-6 bg-gray-50 min-h-screen">
      <FullPageSpinner show={loading} />

      <SalesDetailsModal
        isOpen={saleDetailModal}
        onClose={() => setSaleDetailModal(false)}
        category={activeType}
        date={saleDate}
        sale={sale}
      />

      {/* ✅ Summary Guide Modal */}
      {showSummaryGuide && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 relative">

            {/* Header */}
            <div className="flex justify-between items-center mb-4 border-b pb-2">
              <h3 className="text-lg font-semibold text-gray-800">Sales Calculation Guide</h3>
              <button
                onClick={() => setShowSummaryGuide(false)}
                className="text-gray-400 hover:text-gray-600 transition text-xl font-bold"
                aria-label="Close modal"
              >
                &times;
              </button>
            </div>

            {/* Content */}
            <ul className="text-sm text-gray-700 space-y-3">
              <li className="flex items-start gap-2">
                <span className="mt-0.5">💰</span>
                <div>
                  <strong>Gross Sales:</strong> Total of all sales.
                  <span className="text-gray-500 block">(Pending payments and discount applied are inclusive)</span>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5">📉</span>
                <div>
                  <strong>Net Sales:</strong> Gross Sales - discounts applied - Pending Sales.
                </div>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5">🏷️</span>
                <div>
                  <strong>Discount:</strong> Sum of all discounts given on sales.
                </div>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5">💳</span>
                <div>
                  <strong>Cash / POS / Transfer:</strong> Payment methods breakdown.
                </div>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5">⏳</span>
                <div>
                  <strong>Pending:</strong> Amounts not yet paid or settled.
                </div>
              </li>
            </ul>

            {/* Footer */}
            <div className="mt-6 text-right">
              <button
                onClick={() => setShowSummaryGuide(false)}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Top Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => window.history.back()} className="p-2 rounded-full hover:bg-gray-100 transition" title="Go Back">
            <span className="text-gray-700">{FaArrowLeft({ size: 18 })}</span>
          </button>
          <h1 className="text-2xl font-semibold text-gray-800">Sales Report</h1>
        </div>
        <button
          onClick={handlePrint}
          className="bg-blue-600 text-white px-3 py-2 rounded text-sm flex items-center gap-2 hover:bg-blue-700"
        >
          {FaPrint({ size: 15 })}
          <span className="hidden sm:inline">Generate Report</span>
          <span className="sm:hidden">Print Report</span>
        </button>

      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap justify-between items-center">
        <div className="flex flex-wrap gap-2">
          {/* Category Tabs */}
          <div className="flex flex-wrap justify-between items-center">
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => {
                    setActiveType(cat);
                    // handleApplyDate();
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${activeType === cat
                    ? "bg-blue-600 text-white shadow"
                    : "bg-gray-100 hover:bg-gray-200"
                    }`}
                >
                  {salesMap[cat].title}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Filter Panel */}
      <div className="bg-gray-50 p-4 rounded-lg border space-y-3">

        <div className="flex flex-wrap gap-3 w-full">
          <div className="flex flex-nowrap items-center gap-2 w-full">

            <div className="w-full">
              <input
                type="date"
                value={dateRange.start || ""}
                onChange={(e) => {
                  const newStart = e.target.value;

                  if (dateRange.end && newStart > dateRange.end) {
                    showToast("⚠️ Start date cannot be later than end date.", { type: "warning" });
                    return;
                  }

                  setDateRange({ ...dateRange, start: newStart });
                }}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>

            <div className="w-full">
              <input
                type="date"
                value={dateRange.end || ""}
                onChange={(e) => {
                  const newEnd = e.target.value;

                  if (!dateRange.start) {
                    showToast("Please select a start date first.", { type: "info" });
                    return;
                  }

                  if (newEnd < dateRange.start) {
                    showToast("End date cannot be earlier than start date.", { type: "warning" });
                    return;
                  }

                  setDateRange({ ...dateRange, end: newEnd });
                }}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>

            {/* Apply Button */}
            <button
              onClick={() => handleApplyDate()}
              disabled={!dateRange.start || !dateRange.end}
              className={`flex-shrink-0 px-2 sm:px-3 py-2 rounded-md flex items-center justify-center transition ${!dateRange.start || !dateRange.end
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              title="Apply date filter"
            >
              <span className="hidden sm:inline text-xs sm:text-sm font-medium">Apply</span>
              <span className="sm:hidden flex items-center justify-center">
                {FaCheckCircle({ size: 13 })}
              </span>
            </button>
          </div>

          {/* Instant search */}
          <div className="relative flex-1">
            {/* Search + Filter as single bar */}
            <div className="relative">
              {/* Search input */}
              <input
                type="text"
                placeholder="Search by name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`w-full border rounded px-10 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${activeFilter ? "pr-24" : ""
                  }`}
              />

              {/* Search icon left */}
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                {FaSearch({ size: 16 })}
              </span>

              {/* Show active filter as badge inside input */}
              {activeFilter && activeFilter !== "All" && (
                <span
                  className="absolute right-10 top-1/2 -translate-y-1/2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full cursor-pointer"
                  onClick={() => setActiveFilter("All")}
                >
                  {activeFilter} ✕
                </span>
              )}

              {/* Filter icon right */}
              <button
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800 transition"
                title="Filter Paid / Pending / All"
              >
                {FaFilter({ size: 16 })}
              </button>

              {/* Filter dropdown below search */}
              {showFilterDropdown && (
                <div className="absolute right-0 mt-2 w-40 bg-white border rounded shadow-lg z-50">
                  {["All", "Paid", "Pending"].map((option) => (
                    <button
                      key={option}
                      onClick={() => {
                        setActiveFilter(option);
                        setShowFilterDropdown(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm ${activeFilter === option
                        ? "bg-blue-100 text-blue-600"
                        : "hover:bg-gray-100"
                        }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button onClick={handleClear} className="bg-gray-200 text-gray-800 px-3 py-2 rounded text-sm">Clear</button>
        </div>

        {/* Summary */}
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <span className="inline-block w-1.5 h-5 bg-blue-600 rounded"></span>
            Sales Overview
            {/* Info button */}
            <button
              onClick={() => setShowSummaryGuide(true)}
              className="ml-2 text-gray-400 hover:text-gray-600 transition"
              title="Click for calculation guide"
            >
              {FaInfoCircle({ size: 16 })}
            </button>
          </h2>

          {/* Toggle summary visibility */}
          <button
            onClick={() => setSummaryVisible(!summaryVisible)}
            className="p-1 rounded-full hover:bg-gray-100 transition"
          >
            {summaryVisible
              ? <span>{FaChevronUp({ size: 14 })}</span>
              : <span>{FaChevronDown({ size: 14 })}</span>
            }
          </button>
        </div>

        {/* Sales Cards Grid */}
        {summaryVisible && (
          <div
            className={`grid grid-cols-2 sm:grid-cols-3 ${activeType === "rooms" ? "lg:grid-cols-6" : "lg:grid-cols-5"
              } gap-3 mt-2`}
          >

            {[
              {
                label: "Gross Sales",
                value: (
                  summary.totalPos +
                  summary.totalTransfer +
                  summary.totalCash +
                  summary.totalPending
                  // summary.totalDiscount
                ),
                color: "text-blue-700"
              },
              // { label: "Net Sales", value: summary.totalSold - summary.totalDiscount - summary.totalPending, color: "text-green-700" },
              activeType === "rooms"
                ? { label: "Discount", value: summary.totalDiscount, color: "text-red-600" } : {},
              { label: "Cash", value: summary.totalCash, color: "text-gray-800" },
              { label: "POS", value: summary.totalPos, color: "text-gray-800" },
              { label: "Transfer", value: summary.totalTransfer, color: "text-gray-800" },
              { label: "Pending", value: summary.totalPending, color: "text-gray-800" },
            ].filter(card => !isEmptyObject(card)).map((card) => (
              <div
                key={card.label}
                className="bg-gradient-to-b from-white to-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition transform hover:-translate-y-1"
              >
                <p className="text-gray-500 text-xs font-medium tracking-wide uppercase">
                  {card.label}
                </p>
                <h3 className={`text-lg sm:text-xl font-semibold mt-1 ${card.color}`}>
                  ₦{card.value && card.value.toLocaleString()}
                </h3>
              </div>
            ))}
          </div>
        )}

      </div>

      <div ref={printRef} className="bg-white rounded-xl shadow-sm border overflow-x-auto">
        <Table
          columns={activeType !== "rooms"
            ? [
              { label: "Item", key: "name" },

              { label: "Price", key: "unitPrice", responsive: "md" },

              ...(activeType === "drinks" || activeType === "foods"
                ? [
                  { label: "Opening", key: "opening", responsive: "md" },
                  { label: "Added", key: "stockIn", responsive: "md" },


                  { label: "Spoilage", key: "stockOut", responsive: "md" },
                ]
                : []),

              { label: "Sold", key: "quantitySold" },

              ...(activeType === "drinks" || activeType === "foods"
                ? [
                  { label: "Closing", key: "closing", responsive: "md" },
                ]
                : []),

              { label: "Amount", key: "amount" },
              {
                key: "paymentStatus",
                label: "Status",
                getClassName: (value: any) =>
                  value === "Pending" ? "text-red-500 font-semibold" : "text-green-600 font-semibold",
                responsive: "md",
              },
            ]
            : [
              { label: "Guest", key: "guestName" },
              {
                label: "Room No.", key: "roomNumber", responsive: "md",
                getClassName: () => "uppercase",

              },
              { label: "Booking Type", key: "bookingType", responsive: "md" },
              { label: "CheckIn", key: "checkInDate" },
              { label: "CheckOut", key: "checkOutDate" },
              { label: "Room Rate", key: "roomRate", responsive: "md" },
              { label: "Discount", key: "discount", responsive: "md" },
              { label: "Amount", key: "amountDisplay", responsive: "md" },
              {
                key: "paymentStatus",
                label: "Payment Status",
                getClassName: (value: any) =>
                  value === "Pending" ? "text-red-500 font-semibold" : "text-green-600 font-semibold",
                responsive: "md",
              },
            ] as any
          }

          data={activeType === "rooms" ? bookingSales as any : orderSales}
          rowsPerPage={PAGINATION_PAGE_NUMBER}
          tableMenu={(row: any, closeMenu) => (
            <TableMenu
              row={row}
              closeMenu={closeMenu}
              options={[
                {
                  label: `Sales Details`,
                  onClick: (r: any) => {
                    if (activeType === "rooms") {
                      const date = r.createdAt.split("T")[0];
                      setSaleDate(`${formatDate(date)}, ${r.time}`);
                    } else {
                      setSaleDate(formatDate(r?.createdAt));
                    }
                    setSale(r);
                    setSaleDetailModal(true);
                  },
                },

              ]}
            />
          )}
        />
      </div>
    </div>
  );
}
